import { useMemo } from 'react';

import CalendarProps from './type';
import Button from '../../Button';
import IconButton from '../../IconButton';
import Typography from '../../Typography';
import { TucarTheme } from '../../theme';
import CalendarDropdown from '../CalendarDropdown';
import DateSlot from '../DateSlot';
import { getMonthName } from '../utils';

import {
  CalendarArrowFillerStyled,
  CalendarButtonsContainerStyled,
  CalendarContainerStyled,
  CalendarContentStyled,
  CalendarContentWrapperStyled,
  CalendarHeaderStyled,
  CalendarHeaderCenterStyled,
  CalendarLabelsStyled,
} from './styled';

const Calendar = ({ ...props }: CalendarProps) => {
  const {
    anchorOrigin,
    desktopMode,
    disabled,
    filteredViews,
    handleCancel,
    handleConfirmation,
    handleDisableDate,
    isOpen,
    isWeekly,
    maxDate,
    minDate,
    possibleDates,
    handleSelectedValuesChange,
    selectedValues,
    setSelectedView,
    setIsOpen,
    setDebouncedValue,
    value,
    views,
    ...rest
  } = props;

  const calendarLabels = useMemo(() => ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sáb', 'Dom'], []);
  const allowedValues: ('year' | 'month')[] = useMemo(() => ['year', 'month'], []);
  const {
    range: selectedRange,
    month: selectedMonth,
    year: selectedYear,
    date: selectedDate,
  } = selectedValues || {};

  const checkIfSelectedDate = (date: Date) => {
    const { startDate, endDate } = selectedRange || {};
    if (isWeekly)
      return (
        date?.toDateString() === startDate?.toDateString() ||
        date?.toDateString() === endDate?.toDateString()
      );
    return selectedDate ? selectedDate.toDateString() === date.toDateString() : false;
  };

  const checkDateSlotDisabledStatus = (dayValue: Date, dayDisabled: boolean) => {
    if (dayDisabled) return true;
    return handleDisableDate(dayValue);
  };

  return (
    <CalendarContainerStyled
      data-testid="calendar-container"
      desktopMode={desktopMode}
      anchorOrigin={anchorOrigin}
    >
      <CalendarHeaderStyled>
        {filteredViews.includes('month') &&
        !(!filteredViews.includes('year') && selectedMonth === 0) ? (
          <IconButton
            data-testid="previous-month"
            iconProps={{
              name: 'keyboardArrowLeftRounded',
              color: TucarTheme.colors.blue500,
            }}
            onClick={() => {
              if (selectedMonth === 0) {
                handleSelectedValuesChange({ year: selectedYear - 1, month: 11 });
              } else {
                handleSelectedValuesChange({ month: selectedMonth - 1 });
              }
            }}
            style={{
              padding: '0px',
              height: '19px',
              width: 'auto',
            }}
            variant="ghost"
          />
        ) : (
          <CalendarArrowFillerStyled />
        )}
        <CalendarHeaderCenterStyled>
          <Typography color={TucarTheme.colors.gray800}>
            {getMonthName(selectedMonth)} {selectedYear}
          </Typography>
          <IconButton
            data-testid="dropdown-button"
            iconProps={{
              name: 'arrowDownSpecial',
              color: TucarTheme.colors.blue500,
            }}
            onClick={() =>
              setIsOpen((previousStatus: boolean) => {
                if (
                  !previousStatus &&
                  !allowedValues.some((currentView) => views.includes(currentView))
                )
                  return false;
                return !previousStatus;
              })
            }
            style={{
              ...(isOpen
                ? { transform: 'rotate(180deg)', transitionDuration: '0.5s' }
                : { transform: 'rotate(360deg)', transitionDuration: '0.5s' }),
              ...(!disabled && { cursor: 'pointer' }),
              padding: '0px',
              height: '19px',
              width: 'auto',
            }}
            variant="ghost"
          />
        </CalendarHeaderCenterStyled>
        {filteredViews.includes('month') &&
        !(!filteredViews.includes('year') && selectedMonth === 11) ? (
          <IconButton
            data-testid="next-month"
            iconProps={{
              name: 'keyboardArrowRightRounded',
              color: TucarTheme.colors.blue500,
            }}
            onClick={() => {
              if (selectedMonth === 11) {
                handleSelectedValuesChange({ year: selectedYear + 1, month: 0 });
              } else {
                handleSelectedValuesChange({ month: selectedMonth + 1 });
              }
            }}
            style={{
              padding: '0px',
              height: '19px',
              width: 'auto',
            }}
            variant="ghost"
          />
        ) : (
          <CalendarArrowFillerStyled />
        )}
      </CalendarHeaderStyled>
      <CalendarDropdown
        filteredViews={filteredViews}
        selectedYear={selectedYear}
        selectedDate={selectedDate}
        selectedMonth={selectedMonth}
        setters={{
          setIsOpen,
          setSelectedView,
          setDebouncedValue,
          handleSelectedValuesChange,
        }}
        isOpen={isOpen}
        maxYear={maxDate.getFullYear() || 2099}
        minYear={minDate.getFullYear() || 1900}
        {...rest}
      />
      <CalendarContentWrapperStyled>
        <CalendarLabelsStyled>
          {calendarLabels.map((calendarLabel) => (
            <Typography
              color={TucarTheme.colors.gray700}
              key={calendarLabel}
              style={{
                fontSize: '12px',
                lineHeight: '120%',
                alignItems: 'center',
                justifyContent: 'center',
                display: 'flex',
                maxWidth: '35px',
                minWidth: '24px',
                flexBasis: '14.2857%',
              }}
            >
              {calendarLabel}
            </Typography>
          ))}
        </CalendarLabelsStyled>
        <CalendarContentStyled>
          {possibleDates.map(
            ({ keyValue: dayKey, disabled: dayDisabled, label: dayLabel, value: dayValue }) => {
              const { startDate, endDate } = selectedRange || { startDate: null, endDate: null };
              return (
                <DateSlot
                  data-testid={dayKey}
                  disabled={checkDateSlotDisabledStatus(dayValue, dayDisabled)}
                  key={dayKey}
                  label={dayLabel}
                  multipleMode={
                    isWeekly &&
                    dayValue.getTime() >= startDate?.getTime() &&
                    dayValue.getTime() <= endDate?.getTime()
                  }
                  onClick={() =>
                    selectedDate === dayValue
                      ? handleSelectedValuesChange({ date: null })
                      : handleSelectedValuesChange({ date: dayValue })
                  }
                  selected={checkIfSelectedDate(dayValue)}
                  value={dayValue}
                />
              );
            },
          )}
        </CalendarContentStyled>
      </CalendarContentWrapperStyled>

      <CalendarButtonsContainerStyled>
        <Button onClick={handleCancel} style={{ padding: '12px' }} variant="ghost">
          Cancelar
        </Button>
        <Button
          disabled={
            !selectedDate ||
            (isWeekly && !selectedRange) ||
            selectedDate === value ||
            handleDisableDate(selectedDate)
          }
          onClick={handleConfirmation}
          style={{ padding: '12px' }}
        >
          Aceptar
        </Button>
      </CalendarButtonsContainerStyled>
    </CalendarContainerStyled>
  );
};

export default Calendar;
